import React, { useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import GavelIcon from "@mui/icons-material/Gavel";
import { ComplianceClause, extractComplianceMatrix } from "../../api/shcm";

// ── GEA violet palette (matches GEA_v3 design tokens) ──────────────────────
const V = {
    50: "#EEEEF5",
    100: "#D9D9EE",
    300: "#9292CF",
    600: "#3D3D8F",
    700: "#000028",
};

function PositionChip({ position }: { position: string }) {
    const p = (position || "").toLowerCase();
    let color: "success" | "error" | "warning" | "default" = "default";
    if (p === "accept") color = "success";
    else if (p === "reject") color = "error";
    else if (p === "tailor") color = "warning";
    else if (p === "missing") color = "default";

    return (
        <Chip
            label={position || "—"}
            color={color}
            size="small"
            sx={{ fontWeight: 600, fontSize: 11 }}
        />
    );
}

function StatusChip({ status }: { status: string }) {
    const isCurrent = (status || "").toLowerCase() === "current";
    return (
        <Chip
            label={status || "—"}
            size="small"
            variant={isCurrent ? "filled" : "outlined"}
            sx={{
                fontSize: 11,
                fontWeight: 600,
                backgroundColor: isCurrent ? V[50] : "transparent",
                color: isCurrent ? V[600] : "#888",
                borderColor: isCurrent ? V[300] : "#ccc",
            }}
        />
    );
}

function downloadCSV(clauses: ComplianceClause[], filename: string) {
    const headers = [
        "Clause",
        "Title",
        "Date",
        "Notes",
        "Position",
        "Response Language",
        "Status",
        "Page",
    ];
    const rows = clauses.map((c) => [
        c.clause,
        c.title,
        c.date || c.clause_date || "",
        c.notes || "",
        c.position,
        c.response,
        c.status,
        c.page_number,
    ]);
    const csv = [headers, ...rows]
        .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
        .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export default function SHCMPage() {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [clauses, setClauses] = useState<ComplianceClause[] | null>(null);

    const handleFile = (file: File) => {
        if (!file.name.toLowerCase().endsWith(".pdf")) {
            setError("Please upload a PDF file.");
            return;
        }
        setSelectedFile(file);
        setError(null);
        setClauses(null);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) handleFile(file);
    };

    const handleRun = async () => {
        if (!selectedFile) return;
        setLoading(true);
        setError(null);
        setClauses(null);
        try {
            const result = await extractComplianceMatrix(selectedFile);
            setClauses(result.clauses);
        } catch (e: any) {
            setError(e.message || "Extraction failed.");
        } finally {
            setLoading(false);
        }
    };

    const missCount = clauses?.filter((c) => c.position === "missing").length ?? 0;
    const acceptCount = clauses?.filter((c) => c.position?.toLowerCase() === "accept").length ?? 0;
    const rejectCount = clauses?.filter((c) => c.position?.toLowerCase() === "reject").length ?? 0;

    return (
        <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
            {/* ── Header ── */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                <GavelIcon sx={{ color: V[600], fontSize: 28 }} />
                <Box>
                    <Typography variant="h5" fontWeight={700} color={V[700]}>
                        SHCM — FAR / DFARS Compliance Matrix
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Upload a contract PDF to extract FAR & DFARS clauses and map them against
                        the Intel Library.
                    </Typography>
                </Box>
            </Box>

            {/* ── Upload zone ── */}
            <Paper
                variant="outlined"
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                    border: `2px dashed ${dragOver ? V[600] : V[300]}`,
                    borderRadius: 2,
                    p: 4,
                    textAlign: "center",
                    cursor: "pointer",
                    backgroundColor: dragOver ? V[50] : "#fff",
                    transition: "all 150ms",
                    mb: 2,
                    "&:hover": { backgroundColor: V[50], borderColor: V[600] },
                }}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    hidden
                    onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                />
                <Box
                    sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        backgroundColor: V[600],
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                    }}
                >
                    <CloudUploadIcon sx={{ color: "#fff", fontSize: 24 }} />
                </Box>
                <Typography fontWeight={600} color={V[600]}>
                    {selectedFile ? selectedFile.name : "Drop contract PDF here or click to browse"}
                </Typography>
                {selectedFile && (
                    <Typography variant="caption" color="text.secondary">
                        {(selectedFile.size / 1024).toFixed(0)} KB
                    </Typography>
                )}
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* ── Run button ── */}
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 3 }}>
                <Button
                    variant="contained"
                    disabled={!selectedFile || loading}
                    onClick={handleRun}
                    sx={{
                        backgroundColor: V[600],
                        "&:hover": { backgroundColor: V[700] },
                        fontWeight: 700,
                        px: 4,
                    }}
                >
                    {loading ? "Extracting…" : "Run SHCM Extraction"}
                </Button>
                {loading && <CircularProgress size={22} sx={{ color: V[600] }} />}
                {loading && (
                    <Typography variant="body2" color="text.secondary">
                        Running OCR + Claude extraction — this may take a few minutes for large
                        contracts.
                    </Typography>
                )}
            </Box>

            {/* ── Results ── */}
            {clauses && (
                <>
                    {/* Summary chips */}
                    <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
                        <Typography fontWeight={700} color={V[700]}>
                            {clauses.length} clauses found
                        </Typography>
                        <Chip label={`${acceptCount} Accept`} color="success" size="small" />
                        <Chip label={`${rejectCount} Reject`} color="error" size="small" />
                        {missCount > 0 && (
                            <Chip label={`${missCount} Not in library`} color="default" size="small" />
                        )}
                        <Box sx={{ ml: "auto" }}>
                            <Button
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={() =>
                                    downloadCSV(
                                        clauses,
                                        `${selectedFile?.name.replace(".pdf", "")}_compliance_matrix.csv`
                                    )
                                }
                                sx={{ color: V[600], borderColor: V[300] }}
                                variant="outlined"
                            >
                                Export CSV
                            </Button>
                        </Box>
                    </Box>

                    {/* Table */}
                    <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{ borderRadius: 2, maxHeight: "62vh", overflow: "auto" }}
                    >
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow sx={{ "& th": { backgroundColor: V[700], color: "#fff", fontWeight: 700, fontSize: 12 } }}>
                                    <TableCell sx={{ minWidth: 110 }}>Clause</TableCell>
                                    <TableCell sx={{ minWidth: 200 }}>Title</TableCell>
                                    <TableCell sx={{ minWidth: 90 }}>Date</TableCell>
                                    <TableCell sx={{ minWidth: 180 }}>Notes</TableCell>
                                    <TableCell sx={{ minWidth: 100 }}>Position</TableCell>
                                    <TableCell sx={{ minWidth: 260 }}>Response Language</TableCell>
                                    <TableCell sx={{ minWidth: 90 }}>Status</TableCell>
                                    <TableCell sx={{ minWidth: 60 }}>Pg.</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clauses.map((c, i) => (
                                    <TableRow
                                        key={i}
                                        sx={{
                                            backgroundColor: i % 2 === 0 ? "#fff" : V[50],
                                            "&:hover": { backgroundColor: V[100] },
                                        }}
                                    >
                                        <TableCell
                                            sx={{
                                                fontFamily: "monospace",
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: V[600],
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {c.clause}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>{c.title}</TableCell>
                                        <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                                            {c.date || c.clause_date || "—"}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 12, color: "#555" }}>
                                            {c.notes || "—"}
                                        </TableCell>
                                        <TableCell>
                                            <PositionChip position={c.position} />
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 12 }}>
                                            <Tooltip
                                                title={c.response || ""}
                                                placement="top-start"
                                                arrow
                                            >
                                                <Box
                                                    sx={{
                                                        maxWidth: 260,
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        cursor: c.response ? "help" : "default",
                                                    }}
                                                >
                                                    {c.response || "—"}
                                                </Box>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <StatusChip status={c.status} />
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 12, color: "#888" }}>
                                            {c.page_number || "—"}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    );
}
